import React from "react";
import styled from "styled-components";
import * as d3 from "d3";
import {
  field80Percentile,
  fieldMedian,
  fieldMin,
} from "./utils";

const StyledTooltipContainer = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  color: white;
  font-size: 12px;
  padding: 6px;
  transform: translate(-50%, -50%);
  font-family: "AllianceNo2-Regular" !important;
  z-index: 2000;
`;

const TooltipHeader = styled.div`
  font-family: "AllianceNo2-SemiBold" !important;
  font-size: 13px;
  font-weight: 100 !important;
`;

const TooltipContent = styled.div`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TooltipColor = styled.div`
  background-color: ${(props) => props.bgColor};
  width: 12px;
  height: 12px;
  border: 1px solid #999;
`;

const TooltipKey = styled.div`
  margin: 0 4px;
  text-overflow: ellipsis;
  width: 190px;
  white-space: nowrap;
  overflow: hidden;
`;

const TooltipValue = styled.div`
  margin-right: 10px;
  text-align: right;
  width: 80px;
  white-space: nowrap;
`;

const TooltipLabel = styled.div`
  width: 190px;
  display: flex;
  align-items: center;
`;

const TooltipLabelTotal = styled.div`
  width: 100%;
  text-align: right;
  padding-right: 10px;
`;

const TooltipContentWrapper = styled.div`
  width: ${(props) => (props.large ? "560px" : "280px")};
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  height: ${(props) => props.height + "px"};
  @media only screen and (max-width: 1200px) {
    width: 280px;
  }
`;

const TooltipContentTotal = styled.div`
  margin: 0 0 2px;
  display: block;
  width: 100%;
`;

const getCorrection = (mouseX, chartWidth, isSmallScreen) => {
  if (isSmallScreen) {
    const isLeftSide = mouseX < chartWidth * 0.4;
    return isLeftSide ? 260 : -100;
  }

  const isLeftSide = mouseX < chartWidth * 0.25;
  if (isLeftSide) {
    return chartWidth / 2 > 400 ? 400 : chartWidth / 2;
  }
  return -290;
};

const getCorrectionExtra = (
  mouseX,
  chartWidth,
  isSmallScreen,
  hasManySeries
) => {
  if (isSmallScreen) {
    const isLeftSide = mouseX < chartWidth * 0.4;
    return isLeftSide ? 160 : -100;
  }

  const isLeftSide = mouseX < chartWidth * 0.25;
  if (isLeftSide) {
    return chartWidth / 2 > 400 ? 400 : chartWidth / 2;
  }
  return -290;
};

function sortSeries(input) {
  const tmp = input.sort((a, b) => {
    if (a.value > b.value) {
      return 1;
    } else if (a.value < b.value) {
      return -1;
    } else {
      return 0;
    }
  });

  const center = Math.floor(input.length / 2);
  const output = [];
  for (let i = 0; i < center; i++) {
    output.push(input[i], input[center + i]);
  }

  if (center % 2 === 1) {
    output.push(input[input.length - 1]);
  }

  return tmp;
}

const EntryTotal = ({ valueTotal, unit }) => {
  return (
    <TooltipContentTotal>
      <TooltipLabelTotal key="tootltip-total">
        {valueTotal === "n/a"
          ? "Total n/a"
          : `Total ${d3.format(".3f")(valueTotal)} ${unit}`}
      </TooltipLabelTotal>
    </TooltipContentTotal>
  );
};

const Entry = ({ label, value, color, unit }) => {
  return (
    <TooltipContent key={`${label}-value`}>
      <TooltipLabel>
        <TooltipColor bgColor={color}></TooltipColor>
        <TooltipKey>{label}</TooltipKey>
      </TooltipLabel>
      <TooltipValue>
        {value === "n/a" ? "n/a" : `${d3.format(".3f")(value)} ${unit}`}
      </TooltipValue>
    </TooltipContent>
  );
};

export const TooltipExtra = ({
  dataPoint,
  mouseX,
  mouseY,
  title,
  mouseEvent,
  unit,
  chartWidth,
  fieldsMap,
  filterFields,
  type,
}) => {
  const { time, value } = dataPoint || {};
  if (!time || !fieldsMap) {
    return null;
  }
  const isDown = type.startsWith("down");
  const key = isDown ? type.slice(4, type.length) : type.slice(2, type.length);

  const currentFields = Object.entries(value).reduce(
    (acc, [fieldName, fieldValue]) => {
      if (
        fieldName !== "time" &&
        fieldName !== "rest" &&
        fieldName !== "total" &&
        filterFields.has(fieldName)
      ) {
        const { color } = fieldsMap[fieldName] ?? {};
        acc.series.push({
          color,
          name: fieldName,
          [type]: fieldValue ? fieldValue[type] : undefined,
        });
      }
      if (fieldName === "total" && filterFields.has("total")) {
        const { color } = fieldsMap["total"] ?? {};
        acc.total = {
          color,
          [type]: fieldValue ? fieldValue[key] : undefined,
        };
      }
      if (fieldName === "rest" && filterFields.has("rest")) {
        const { color } = fieldsMap["rest"] ?? {};
        acc.rest = {
          color,
          [type]: fieldValue ? fieldValue[key] : undefined,
        };
      }
      return acc;
    },
    { series: [], rest: undefined, total: undefined }
  );

  const isSmallScreen = chartWidth < 650;
  const correction = getCorrectionExtra(
    mouseX,
    chartWidth,
    isSmallScreen,
    currentFields.series.length > 9
  );

  const height = (filterFields.size + 1) * 19;

  return (
    <StyledTooltipContainer
      style={{
        top: mouseY - 105 + "px",
        left: mouseX + correction + "px",
      }}
      className="tooltip-live"
    >
      <TooltipHeader>{title}</TooltipHeader>
      <TooltipContentWrapper large={false} height={height}>
        {currentFields.total ? (
          <Entry
            label="TOTAL"
            color={currentFields.total.color}
            value={currentFields.total[type]}
            key="tooltip-total"
            unit={unit}
          />
        ) : null}
        {value.total && value.total.rtt80 && filterFields.has("rtt80") ? (
          <Entry
            label={field80Percentile.label}
            color={field80Percentile.color}
            value={value.total.rtt80}
            key="tooltip-p80"
            unit={"ms"}
          />
        ) : null}    
        {value.total && value.total.rttmed && filterFields.has("rttmed") ? (
          <Entry
            label={fieldMedian.label}
            color={fieldMedian.color}
            value={value.total.rttmed}
            key="tooltip-med"
            unit={"ms"}
          />
        ) : null}
        {value.total && value.total.rttmin && filterFields.has("rttmin") ? (
          <Entry
            label={fieldMin.label}
            color={fieldMin.color}
            value={value.total.rttmin}
            key="tooltip-min"
            unit={"ms"}
          />
        ) : null}
        {currentFields.rest ? (
          <Entry
            label="REST"
            color={currentFields.rest.color}
            value={currentFields.rest[type]}
            key="tooltip-rest"
            unit={unit}
          />
        ) : null}
        {sortSeries(currentFields.series).map((serie, index) => {
          const { name, color } = serie;
          const valueType = serie[type];
          if (valueType === null || (isSmallScreen && index > 9)) {
            return null;
          }
          return (
            <Entry
              label={name}
              value={valueType}
              color={color}
              key={`tooltip-${name}`}
              unit={unit}
            />
          );
        })}
      </TooltipContentWrapper>
    </StyledTooltipContainer>
  );
};

const Tooltip = ({
  dataPoint,
  mouseX,
  mouseY,
  title,
  mouseEvent,
  unit,
  chartWidth,
  fieldsMap,
  filterFields,
  type,
  top20,
}) => {
  const { time, value, totalLink } = dataPoint || {};
  if (!time || !fieldsMap) {
    return null;
  }

  const currentFields = Object.entries(value).reduce(
    (acc, [fieldName, fieldValue]) => {
      if (
        fieldName !== "time" &&
        fieldName !== "rest" &&
        filterFields.has(fieldName) &&
        top20.has(fieldName)
      ) {
        const { color } = fieldsMap[fieldName] ?? {};
        acc.series.push({
          color,
          name: fieldName,
          value: fieldValue[`${type}link`],
        });
      }
      if (fieldName === "rest" && filterFields.has("rest")) {
        const { color } = fieldsMap["rest"] ?? {};
        acc.rest = { color, value: fieldValue[`${type}link`] };
      }
      return acc;
    },
    { series: [], rest: undefined, total: undefined }
  );

  const isSmallScreen = chartWidth < 700;
  const correction = getCorrection(mouseX, chartWidth, isSmallScreen);

  const height =
    currentFields.series.length > 9
      ? Math.ceil((currentFields.series.length + 1) / 2) * 19
      : (currentFields.series.length + 1) * 19;

  return (
    <StyledTooltipContainer
      style={{
        top: mouseY - 105 + "px",
        left: mouseX + correction + "px",
      }}
      className="tooltip-live"
    >
      <TooltipHeader>{title}</TooltipHeader>
      <TooltipContentWrapper
        large={!isSmallScreen && currentFields.series.length > 9}
        height={height}
      >
        {currentFields.rest ? (
          <Entry
            label="REST"
            color={currentFields.rest.color}
            value={currentFields.rest.value}
            key="tooltip-rest"
            unit={unit}
          />
        ) : null}
        {sortSeries(currentFields.series).map(
          ({ name, color, value }, index) => {
            if (value === null || (isSmallScreen && index > 9)) {
              return null;
            }
            return (
              <Entry
                label={name}
                value={value}
                color={color}
                key={`tooltip-${name}`}
                unit={unit}
              />
            );
          }
        )}
      </TooltipContentWrapper>
      {currentFields.series.length > 0 ? (
        <EntryTotal valueTotal={totalLink} key="tooltip-total" unit={unit} />
      ) : null}
    </StyledTooltipContainer>
  );
};

export default Tooltip;
